import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSnackbar } from 'notistack';
// @mui
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
// hook
import { useBoolean } from 'hooks';
// api
import { changeCurrentUserPassword } from 'utils/apis';
// 
// component
import FormProvider, { RHFTextField } from 'components/hook-form';
// 
import { SLoadingButton } from './Base';
import { getErrorMessage } from 'utils/utils';
import { useLocales } from 'locales';
import { useResponsive } from 'hooks/use-responsive';

type props = {
    open: boolean;
    onClose: () => void;
};

const PasswordModal = ({ open, onClose }: props) => {
    const { t } = useLocales();
    const { enqueueSnackbar } = useSnackbar();
    const isMobile = useResponsive();

    const isSubmitting = useBoolean();

    const PasswordSchema = Yup.object().shape({
        password: Yup.string().min(4).required(t('Password is required')),
        confirm: Yup.string().required(t('Confirm Password is required'))
    });

    const defaultValues = {
        password: '',
        confirm: ''
    };

    const methods = useForm({
        resolver: yupResolver(PasswordSchema),
        defaultValues
    });

    const {
        setError,
        handleSubmit
    } = methods;

    const onSubmit = handleSubmit(async (data) => {
        try {
            if (data.password !== data.confirm) {
                setError("confirm", { message: t('Invalid password') })
                return;
            }
            isSubmitting.onTrue();
            await changeCurrentUserPassword({ 
                newPassword: data.password,
                newPasswordRepeat: data.confirm
            });
            enqueueSnackbar(t('Updated successfully!'));
            onClose();
            isSubmitting.onFalse();
        } catch (error: any) {
            enqueueSnackbar(getErrorMessage(error), { variant: 'error' })
            isSubmitting.onFalse();
        }
    });

    return (
        <Dialog open={open} onClose={onClose} maxWidth={isMobile ? "lg" : "sm"} fullWidth sx={{
            '.MuiDialog-paper': {
                borderRadius: 0,
                borderStyle: 'solid',
                borderColor: 'secondary.main'
            }
        }}>
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    bgcolor: 'background.default',
                    borderStyle: 'none none solid none',
                    borderColor: 'secondary.main'
                }}>
                {t("Change Password")}
                <IconButton
                    onClick={onClose}
                    sx={{
                        bgcolor: 'background.paper', borderRadius: 0, width: 30, height: 30, border: '3px solid #6a6b70'
                    }}>
                    X
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Box sx={{ pt: 3 }}>
                    <FormProvider methods={methods} onSubmit={onSubmit}>
                        <Box
                            rowGap={3}
                            columnGap={2}
                            display="grid"
                            gridTemplateColumns={{
                                xs: 'repeat(1, 1fr)',
                                sm: 'repeat(2, 1fr)'
                            }}
                        >
                            <RHFTextField
                                size='small'
                                type='password'
                                name="password"
                                label={t("Password")}
                            />
                            <RHFTextField
                                size='small'
                                type='password'
                                name="confirm"
                                label={t("Confirm Password")}
                            />
                            <SLoadingButton variant='contained' color="error" type="submit" loading={isSubmitting.value}>
                                {t("Submit")}
                            </SLoadingButton>
                        </Box>
                    </FormProvider>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default PasswordModal;
